/**
 * Validation Page SignalR Handler Composable
 *
 * Responsibilities:
 * - Handle real-time SignalR events for validation page
 * - Process barcode change events (sample merges/updates)
 * - Process sample result events (added/deleted/modified)
 * - Prevent conflicts with user actions (race condition protection)
 * - Manage automatic sample removal when results depleted
 * - Coordinate with polling workaround until backend fix
 * - Maintain SignalR subscription lifecycle
 *
 * Use Cases:
 * - User A merges samples → User B sees updated barcode
 * - Lab instrument adds results → All users see new results
 * - User A validates sample → User B sees removal from list
 * - Sample becomes empty → Automatically navigate to next
 *
 * TEMPORARY:
 * - Polling workaround: Mark new samples until backend sends full metadata
 * - Will be removed: Once backend SignalR sends complete sample data
 */

//
// ============================================================================
// IMPORTS
// ============================================================================
//

// Types
import type { PendingSample } from "../../../services/api/domains/pending/pending.types";

// TEMPORARY: Polling composable
import { useValidationPolling } from "./useValidationPolling";
import type { useValidationSelection } from "./useValidationSelection";

export const useValidationSignalR = (
  key: string = "default",
  isUpdatingBarcode: Ref<boolean>,
  filteredSamples: ComputedRef<PendingSample[]>,
  selectedSample: Ref<PendingSample | null>,
  selectSample: (sample: PendingSample | null, forceRefresh: boolean) => Promise<void>,
  fetchSamples: (forceRefresh: boolean, silent: boolean) => Promise<void>,
  fetchSampleData: (barcode: string, forceRefresh: boolean, isSilent: boolean) => Promise<void>,
  isPerformingResultAction: Ref<Set<string>>,
  pendingComposable: ReturnType<typeof usePending>,
  selectionComposable: ReturnType<typeof useValidationSelection>,
) => {
  //
  // ============================================================================
  // COMPOSABLES
  // ============================================================================
  //

  // Localization
  const { t } = useI18n();

  // Snackbar
  const { showSnackbar } = useSnackbar();

  // SignalR
  const { subscribe, connect, getHubState, EVENTS } = useSignalR();

  // TEMPORARY: Pooling Composable (until backend sends full sample metadata)
  const pollingComposable = useValidationPolling(key);

  // Get runtime configuration
  const config = useRuntimeConfig();

  //
  // ============================================================================
  // STATE
  // ============================================================================
  //
  const isConnected = getHubState("MASTER");
  const initialized = useState<boolean>(`${key}.signalr.initialized`, () => false);

  /**
   * Connection state tracking
   * - disconnected: Initial state or after connection lost
   * - connecting: Attempting to establish connection
   * - connected: Successfully connected and ready
   * - reconnecting: Attempting to restore lost connection
   */
  const connectionState = useState<"disconnected" | "connecting" | "connected" | "reconnecting">(
    `${key}.signalr.connectionState`,
    () => "disconnected",
  );

  /**
   * Reconnection attempt tracking for exponential backoff
   *
   * Configured via: NUXT_PUBLIC_SIGNALR_MAX_RECONNECT_ATTEMPTS (default: 5)
   */
  const reconnectAttempts = ref(0);
  const maxReconnectAttempts = config.public.signalr.maxReconnectAttempts;

  /**
   * Base delay for exponential backoff reconnection
   *
   * Configured via: NUXT_PUBLIC_SIGNALR_BASE_RECONNECT_DELAY (default: 1000ms)
   */
  const baseReconnectDelay = config.public.signalr.baseReconnectDelay;

  //
  // ============================================================================
  // COMPUTED PROPERTIES
  // ============================================================================
  //

  /**
   * Check if currently in connecting phase (initial connection on page load)
   */
  const isConnecting = computed(() => connectionState.value === "connecting");

  /**
   * Check if currently reconnecting (after connection loss)
   */
  const isReconnecting = computed(() => connectionState.value === "reconnecting");

  /**
   * Check if connection is being established (connecting OR reconnecting)
   * Use this for loading indicators that should show during both initial and reconnection
   */
  const isEstablishingConnection = computed(
    () => connectionState.value === "connecting" || connectionState.value === "reconnecting",
  );

  /**
   * Stores cleanup functions returned by subscribe().
   * Called during cleanup to remove event handlers.
   *
   * Each function removes a specific subscription.
   */
  const cleanupFunctions = ref<Array<() => void>>([]);

  //
  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================
  //

  /**
   * Handle barcode change event from SignalR
   *
   * Purpose:
   * - React to barcode changes (sample merge or update)
   * - Refresh affected samples and results
   * - Re-select or navigate after change
   * - Prevent conflicts with user editing
   *
   * Responsibilities:
   * - Check conflict: Skip if user is editing barcode OR reconnecting
   * - Clear cache: Old and new barcodes (merge case!)
   * - Refetch samples: Get updated sample list
   * - Handle selection:
   *   - Old selected: Re-select with new barcode
   *   - New selected: Refresh data (merge case)
   * - Show notification: Inform user of change
   *
   * @param data - Event data with old and new barcode
   */
  const handleBarcodeChange = async (data: { oldBarcode: string; newBarcode: string }) => {
    // Prevent race condition if user is editing barcode
    if (isUpdatingBarcode.value) {
      return;
    }

    // Prevent duplicate fetches during reconnection window
    if (isReconnecting.value) {
      return;
    }

    // Track if old or new barcode was selected
    const oldWasSelected = selectedSample.value?.barcode === data.oldBarcode;
    const newWasSelected = selectedSample.value?.barcode === data.newBarcode;

    // Clear cached results for OLD barcode
    // This ensures we don't show stale data after merge
    const oldKey = data.oldBarcode.toUpperCase();
    if (pendingComposable.sampleResults.value[oldKey]) {
      delete pendingComposable.sampleResults.value[oldKey];

      if (import.meta.client) {
        sessionStorage.setItem(`pending-results-pending`, JSON.stringify(pendingComposable.sampleResults.value));
      }
    }

    // Also clear cache for NEW barcode (merge case!)
    // This ensures we fetch fresh merged results
    const newKey = data.newBarcode.toUpperCase();
    if (pendingComposable.sampleResults.value[newKey]) {
      delete pendingComposable.sampleResults.value[newKey];

      if (import.meta.client) {
        sessionStorage.setItem(`pending-results-pending`, JSON.stringify(pendingComposable.sampleResults.value));
      }
    }

    // Refetch samples
    await fetchSamples(true, true);

    // Case 1: Old barcode was selected → Re-select with new barcode
    if (oldWasSelected) {
      const updatedSample = filteredSamples.value.find((s) => s.barcode === data.newBarcode);

      if (updatedSample) {
        // Update selection state with fresh sample metadata
        selectionComposable.setSelectedSample(updatedSample);

        // Silently refresh sample data (results + visit) WITHOUT aborting or showing loader
        await fetchSampleData(data.newBarcode, true, true); // forceRefresh=true, isSilent=true
      }
    }
    // Case 2: New barcode was selected → Refresh data (merge case!)
    else if (newWasSelected) {
      // Clear data FIRST to prevent showing stale data
      selectionComposable.clearData();

      // Update selection state with fresh sample metadata from refetched list
      const updatedSample = filteredSamples.value.find((s) => s.barcode === data.newBarcode);

      if (updatedSample) {
        // Update selection metadata
        selectionComposable.setSelectedSample(updatedSample);

        // Fetch fresh data (NOT silent - shows loading to prevent flash)
        await fetchSampleData(updatedSample.barcode, true, false);
      }
    }

    // Show notification
    showSnackbar(
      t("pages.validation.signalr.barcodeChanged", {
        old: data.oldBarcode,
        new: data.newBarcode,
      }),
      { icon: "mdi-xml", color: "info" },
    );
  };

  /**
   * Handle sample results change event from SignalR
   *
   * Purpose:
   * - React to result changes (added/deleted/modified)
   * - Remove empty samples automatically
   * - Refresh affected sample data
   * - Prevent conflicts with user actions
   *
   * Responsibilities:
   * - Check conflict: Skip if user is acting on this sample OR reconnecting
   * - Handle deletions: Check if sample became empty → remove
   * - Handle additions: Detect new samples → refetch list
   * - Refresh selection: Update if affected sample selected
   * - Navigate: Move to next if selected sample removed
   * - Show notifications: Inform user of changes
   *
   * @param data - Event data with sample ID and result changes
   */
  const handleSampleResults = async (data: {
    sampleId: string;
    addedResults?: any[];
    deletedResults?: any[];
    modifiedResults?: any[];
  }) => {
    const { sampleId, deletedResults, modifiedResults } = data;

    // Prevent conflicts while user is acting on this sample
    if (isPerformingResultAction.value.has(sampleId)) {
      return;
    }

    // Prevent duplicate fetches during reconnection window
    if (isReconnecting.value) {
      return;
    }

    const sampleIndex = pendingComposable.samples.value.findIndex((s) => s.barcode === sampleId);

    const sampleExists = sampleIndex !== -1;

    /**
     * Handle possible EMPTY sample after deletions
     */
    if (sampleExists && deletedResults?.length) {
      // Fetch fresh data to verify if sample still has results
      const remainingResults = await pendingComposable.fetchPendingSampleResults(sampleId, true);

      // No results left → remove sample
      if (!remainingResults || remainingResults.length === 0) {
        console.log(`[SignalR] Sample ${sampleId} has no results - removing`);

        // Remove from the ACTUAL samples array (not filteredSamples)
        pendingComposable.samples.value = pendingComposable.samples.value.filter((s) => s.barcode !== sampleId);

        // Also clear result cache
        delete pendingComposable.sampleResults.value[sampleId.toUpperCase()];

        // Update sessionStorage
        if (import.meta.client) {
          sessionStorage.setItem(`pending-samples-pending`, JSON.stringify(pendingComposable.samples.value));
          sessionStorage.setItem(`pending-results-pending`, JSON.stringify(pendingComposable.sampleResults.value));
        }

        // If currently selected → move to next sample
        if (selectedSample.value?.barcode === sampleId) {
          // Use filteredSamples for navigation (respects search/filters)
          const currentIndex = filteredSamples.value.findIndex((s) => s.barcode === sampleId);

          const nextSample =
            filteredSamples.value[currentIndex + 1] || // Try next
            filteredSamples.value[currentIndex - 1] || // Try previous
            filteredSamples.value[0]; // Fallback to first

          if (nextSample) {
            await selectSample(nextSample, false);
          } else {
            // Import selectionComposable as a parameter or use clearSelection
            selectedSample.value = null;
          }
        }

        showSnackbar(
          t("pages.validation.signalr.sampleRemoved", {
            barcode: sampleId,
          }),
          { icon: "mdi-delete-sweep", color: "warning" },
        );

        return;
      }
    }

    /**
     * New sample detected
     */
    if (!sampleExists) {
      await fetchSamples(true, true);

      // TEMP workaround until backend fix
      pollingComposable.markAsNew(sampleId);

      // Removed snackbar - only pulsing background remains
      // showSnackbar(
      //   t("pages.validation.signalr.sampleAdded", {
      //     barcode: sampleId,
      //   }),
      //   { icon: "mdi-test-tube", color: "success" },
      // );
      return;
    }

    /**
     * Refresh selected sample data
     */
    if (selectedSample.value?.barcode === sampleId) {
      await fetchSampleData(sampleId, true, true);
    }

    /**
     * Notifications - Removed for new results (only pulsing background remains)
     */
    // if (addedResults?.length) {
    //   showSnackbar(
    //     t("pages.validation.signalr.resultsAdded", {
    //       count: addedResults.length,
    //       barcode: sampleId,
    //     }),
    //     { icon: "mdi-plus-circle", color: "info" }
    //   );
    // }

    if (deletedResults?.length) {
      showSnackbar(
        t("pages.validation.signalr.resultsDeleted", {
          count: deletedResults.length,
          barcode: sampleId,
        }),
        { icon: "mdi-delete", color: "warning" },
      );
    }

    if (modifiedResults?.length) {
      showSnackbar(
        t("pages.validation.signalr.resultsModified", {
          count: modifiedResults.length,
          barcode: sampleId,
        }),
        { icon: "mdi-pencil", color: "info" },
      );
    }
  };

  //
  // ============================================================================
  // LIFECYCLE MANAGEMENT
  // ============================================================================
  //

  /**
   * Initialize SignalR connection and subscriptions
   *
   * Purpose:
   * - Establish SignalR connection to MASTER hub
   * - Subscribe to validation page events
   * - Store cleanup functions for unmount
   * - Prevent duplicate subscriptions
   * - Handle reconnection with exponential backoff
   *
   * Responsibilities:
   * - Check: If already initialized → skip (idempotent)
   * - Connect: To MASTER SignalR hub with retry logic
   * - Subscribe: Barcode change events
   * - Subscribe: Sample result events
   * - Store: Cleanup functions for later
   * - Handle: Errors with automatic reconnection
   *
   * IMPORTANT:
   * - Call ONCE per key during component setup
   * - Multiple calls prevented by initialized flag
   * - Cleanup automatically called on unmount
   */
  const initializeSignalR = async () => {
    if (initialized.value) {
      return;
    }

    initialized.value = true;
    connectionState.value = "connecting";

    try {
      // Connect to MASTER hub
      await connect(["MASTER"]);

      connectionState.value = "connected";
      reconnectAttempts.value = 0; // Reset on successful connection

      // Subscribe to barcode change events
      // The subscribe() function returns a cleanup function
      const barcodeCleanup = await subscribe(EVENTS.SAMPLE_BARCODE, handleBarcodeChange, "MASTER");

      // Subscribe to sample results events
      const resultsCleanup = await subscribe(EVENTS.SAMPLE_RESULTS, handleSampleResults, "MASTER");

      // Store cleanup functions for later
      // These will be called in cleanupSignalR()
      cleanupFunctions.value = [barcodeCleanup, resultsCleanup];
    } catch (err) {
      connectionState.value = "disconnected";

      // Attempt reconnection with exponential backoff
      if (reconnectAttempts.value < maxReconnectAttempts) {
        reconnectAttempts.value++;
        connectionState.value = "reconnecting";

        // Calculate delay: 1s, 2s, 4s, 8s, 16s
        const delay = baseReconnectDelay * Math.pow(2, reconnectAttempts.value - 1);

        setTimeout(() => {
          initialized.value = false; // Allow retry
          initializeSignalR();
        }, delay);
      } else {
        // Max attempts reached - give up
        initialized.value = false;
        showSnackbar(
          t("pages.validation.signalr.connectionFailed", {
            attempts: maxReconnectAttempts,
          }),
          { icon: "mdi-lan-disconnect", color: "warning", timeout: 5000 },
        );
      }
    }
  };

  /**
   * Cleanup SignalR subscriptions
   *
   * Purpose:
   * - Remove all SignalR event handlers
   * - Prevent memory leaks on unmount
   * - Reset state for re-initialization
   * - Handle cleanup errors gracefully
   *
   * Responsibilities:
   * - Check: If initialized → proceed, else skip
   * - Call: All stored cleanup functions
   * - Handle: Cleanup errors (don't throw)
   * - Clear: Cleanup functions array
   * - Reset: initialized flag to false
   *
   * IMPORTANT:
   * - Automatically called on component unmount (onBeforeUnmount)
   * - Prevents memory leaks from lingering subscriptions
   * - Safe to call multiple times (idempotent)
   */
  const cleanupSignalR = () => {
    if (!initialized.value) {
      return;
    }

    // Call all cleanup functions to unsubscribe
    cleanupFunctions.value.forEach((cleanup) => {
      try {
        cleanup(); // This removes the specific event handler
      } catch (err) {
        // Silent cleanup error - cleanup is best-effort
      }
    });

    // Clear cleanup functions
    cleanupFunctions.value = [];

    // Reset initialized flag
    initialized.value = false;
    connectionState.value = "disconnected";
    reconnectAttempts.value = 0;
  };

  // Automatically cleanup on component unmount
  onBeforeUnmount(() => {
    cleanupSignalR();
  });

  //
  // ============================================================================
  // EXPORTS
  // ============================================================================
  //

  return {
    isConnected: readonly(isConnected),
    connectionState: readonly(connectionState),
    isConnecting: readonly(isConnecting),
    isReconnecting: readonly(isReconnecting),
    isEstablishingConnection: readonly(isEstablishingConnection),

    initializeSignalR,
    cleanupSignalR,
  };
};
